/**
 * Alt-label-editor (M8 §5) — per view-resolver een eigen NL/EN-label voor
 * een concept of relatie. `resolveLabelForViewKind` toont deze labels al in
 * viewpoints met een matchende `label_resolver` (Sharp, C4, BMC); dit blok
 * maakt ze bewerkbaar. Hergebruikt `BilingualField` zodat de auto-translate-
 * on-blur (zoals bij naam/description) gratis meekomt.
 *
 * Herbruikbaar voor concept én relatie: de parent levert de resolver-lijst,
 * de huidige `altLabels`-bag en een `onChange(resolver, lang, value)`-callback
 * die de juiste patch-actie kiest (patchConcept / patchRelation).
 *
 * Default ingeklapt — het is een geavanceerde optie, geen kernveld.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BilingualField } from "./BilingualField";

/** Weergavenaam voor een resolver-id. Proper nouns → niet i18n. */
function resolverDisplayName(resolver: string): string {
  switch (resolver) {
    case "sharp":
      return "Sharp";
    case "c4":
      return "C4";
    case "bmc":
      return "BMC";
    default:
      return resolver.charAt(0).toUpperCase() + resolver.slice(1);
  }
}

interface Props {
  resolvers: string[];
  altLabels: Record<string, Record<string, string>>;
  disabled?: boolean | undefined;
  onChange: (resolver: string, lang: "nl" | "en", value: string) => void;
}

export function AltLabelsSection({ resolvers, altLabels, disabled, onChange }: Props) {
  const { t } = useTranslation();
  // Standaard opengeklapt zodra er al minstens één alt-label gevuld is
  // (zodat bestaande waarden meteen zichtbaar zijn); anders ingeklapt.
  // De initializer draait één keer per mount — de parent keyt dit component
  // op de concept-/relatie-id zodat 't per selectie opnieuw evalueert.
  const [open, setOpen] = useState(() =>
    resolvers.some((r) => {
      const bag = altLabels[r];
      return !!bag && Object.values(bag).some((v) => v && v.length > 0);
    }),
  );
  const diagramsWord = t("attributePanel.altLabels.diagramsWord");
  if (resolvers.length === 0) return null;

  return (
    <section className="rounded border border-(--ld-border)">
      <button
        type="button"
        className="flex w-full items-center justify-between px-2 py-1 text-left font-medium text-(--ld-muted) text-xs hover:bg-(--ld-raised)"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{t("attributePanel.altLabels.title")}</span>
        <span className="text-(--ld-muted)">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="flex flex-col gap-2 px-2 pb-2">
          <p className="text-xs text-(--ld-muted)">{t("attributePanel.altLabels.hint")}</p>
          {resolvers.map((r) => {
            const caption = `${resolverDisplayName(r)} ${diagramsWord}`;
            const bag = altLabels[r] ?? {};
            return (
              <BilingualField
                key={r}
                kind="name"
                labelNl={`${caption} (NL)`}
                labelEn={`${caption} (EN)`}
                valueNl={bag.nl ?? ""}
                valueEn={bag.en ?? ""}
                disabled={disabled}
                onChange={(lang, value) => onChange(r, lang, value)}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
